import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing, Smartphone, ExternalLink } from "lucide-react";
import {
  getSubscriptionStatus,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
  isIOS,
  isIOSStandalone,
  isPushSupported,
} from "@/lib/pushNotifications";
import { toast } from "sonner";

export function NotificationsDialog({ open, onOpenChange }) {
  const [status, setStatus] = useState({ supported: false });
  const [busy, setBusy] = useState(false);
  const supported = isPushSupported();
  const iosBlocked = isIOS() && !isIOSStandalone();

  const refresh = async () => {
    const s = await getSubscriptionStatus();
    setStatus(s);
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const doSubscribe = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notifications enabled");
      refresh();
    } catch (err) {
      toast.error(err.message || "Failed to enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const doUnsubscribe = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      toast.success("Notifications disabled");
      refresh();
    } catch (err) {
      toast.error(err.message || "Failed to disable");
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush();
      toast.success(`Sent test notification (${r.sent}/${r.total})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Failed to send test");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="notifications-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="w-4 h-4 text-primary" /> Background notifications
          </DialogTitle>
          <DialogDescription>
            Get pinged the moment a new lead is captured — even when Call Swiper isn't open.
          </DialogDescription>
        </DialogHeader>

        {!supported && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
            Your browser doesn't support Web Push. Try Chrome/Edge/Firefox on desktop or install Call Swiper on your Home Screen.
          </div>
        )}

        {supported && iosBlocked && (
          <div className="rounded-md border border-primary/25 bg-primary/5 p-3 text-sm space-y-2" data-testid="ios-install-hint">
            <div className="flex items-start gap-2">
              <Smartphone className="w-4 h-4 mt-0.5 text-primary shrink-0" />
              <div>
                <p className="font-semibold">Add Call Swiper to your Home Screen first</p>
                <p className="text-xs text-muted-foreground mt-1">
                  In Safari, tap the <b>Share</b> icon → <b>Add to Home Screen</b>. Open Call Swiper from the new icon, then come back here to enable notifications.
                </p>
              </div>
            </div>
          </div>
        )}

        {supported && !iosBlocked && (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/30 p-3 grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Permission</div>
                <div className="font-semibold capitalize">{status.permission || "default"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Subscription</div>
                <div className="font-semibold">{status.subscribed ? "Active" : "Not subscribed"}</div>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {!status.subscribed ? (
                <Button onClick={doSubscribe} disabled={busy} data-testid="enable-notifications-btn">
                  <Bell className="w-4 h-4 mr-1.5" /> Enable notifications
                </Button>
              ) : (
                <>
                  <Button onClick={doTest} disabled={busy} data-testid="test-notification-btn">
                    <BellRing className="w-4 h-4 mr-1.5" /> Send test
                  </Button>
                  <Button variant="outline" onClick={doUnsubscribe} disabled={busy} data-testid="disable-notifications-btn">
                    <BellOff className="w-4 h-4 mr-1.5" /> Disable
                  </Button>
                </>
              )}
            </div>

            {status.permission === "denied" && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive-foreground">
                Notifications are blocked. Update site permissions in your browser settings, then reload.
              </div>
            )}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground pt-2 border-t border-border">
          Push works on Android (Chrome/Firefox/Edge), macOS, Windows, and iOS 16.4+ Home Screen apps.
        </p>
      </DialogContent>
    </Dialog>
  );
}
